import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Briefcase,
    GraduationCap,
    IdentificationBadge,
    Plus,
    Trash,
    Upload,
    CheckCircle,
    Warning,
    Clock,
} from "@phosphor-icons/react";

const emptyJob = { company: "", title: "", start: "", end: "", description: "" };
const emptyEdu = { degree: "", institute: "", field_of_study: "", start_year: "", end_year: "", grade: "" };

// ---------- Employment + Education section (Phase 2) ---------------------------
export function ProfileEnrichmentSection({ initial }) {
    const [employment, setEmployment] = useState(initial?.employment || []);
    const [education, setEducation] = useState(initial?.education || []);
    const [empDirty, setEmpDirty] = useState(false);
    const [eduDirty, setEduDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Re-sync when parent's profile finishes loading.
    useEffect(() => {
        if (initial?.employment) setEmployment(initial.employment);
        if (initial?.education) setEducation(initial.education);
    }, [initial?.employment, initial?.education]);

    const addJob = () => { setEmployment([...employment, { ...emptyJob }]); setEmpDirty(true); };
    const addEdu = () => { setEducation([...education, { ...emptyEdu }]); setEduDirty(true); };
    const updJob = (i, k, v) => { setEmployment(employment.map((e, idx) => idx === i ? { ...e, [k]: v } : e)); setEmpDirty(true); };
    const updEdu = (i, k, v) => { setEducation(education.map((e, idx) => idx === i ? { ...e, [k]: v } : e)); setEduDirty(true); };
    const rmJob = (i) => { setEmployment(employment.filter((_, idx) => idx !== i)); setEmpDirty(true); };
    const rmEdu = (i) => { setEducation(education.filter((_, idx) => idx !== i)); setEduDirty(true); };

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            if (empDirty) {
                body.employment = employment.filter((j) => j.company && j.title && j.start);
            }
            if (eduDirty) {
                body.education = education.map((e) => ({
                    ...e,
                    start_year: e.start_year ? Number(e.start_year) : null,
                    end_year: e.end_year ? Number(e.end_year) : null,
                })).filter((e) => e.degree && e.institute);
            }
            if (!body.employment && !body.education) {
                toast.info("Nothing to save.");
                setSaving(false);
                return;
            }
            await api.patch("/me/profile/enrichment", body);
            toast.success("Profile details saved.");
            setEmpDirty(false);
            setEduDirty(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-testid="profile-enrichment-section" className="bg-white border border-[#E5E5E5] p-5 sm:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                        Phase 2 · optional
                    </p>
                    <h2 className="font-heading text-2xl font-black tracking-tight text-ink">Employment & Education</h2>
                    <p className="text-xs text-[#525252] mt-1">Recruiters look at profiles with work history 3× more often.</p>
                </div>
            </div>

            {/* Employment */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] inline-flex items-center gap-1.5">
                        <Briefcase size={12} weight="bold" /> Employment
                    </p>
                    <button type="button" data-testid="add-employment-btn" onClick={addJob}
                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:underline inline-flex items-center gap-1">
                        <Plus size={11} weight="bold" /> Add role
                    </button>
                </div>
                {employment.length === 0 && (
                    <p className="text-xs text-[#A3A3A3] mb-2">No roles yet. Add your current + past 2 to look complete.</p>
                )}
                <div className="space-y-3">
                    {employment.map((job, i) => (
                        <div key={i} data-testid={`emp-row-${i}`} className="border border-[#E5E5E5] p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input value={job.company} onChange={(e) => updJob(i, "company", e.target.value)} placeholder="Company" data-testid={`emp-${i}-company`}
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input value={job.title} onChange={(e) => updJob(i, "title", e.target.value)} placeholder="Title" data-testid={`emp-${i}-title`}
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input type="month" value={job.start} onChange={(e) => updJob(i, "start", e.target.value)} placeholder="Start" data-testid={`emp-${i}-start`}
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input type="month" value={job.end || ""} onChange={(e) => updJob(i, "end", e.target.value)} placeholder="End (leave blank if current)" data-testid={`emp-${i}-end`}
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <textarea value={job.description} onChange={(e) => updJob(i, "description", e.target.value)} placeholder="What did you do here? (optional)" rows={2}
                                className="sm:col-span-2 px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt resize-none" />
                            <button type="button" onClick={() => rmJob(i)} data-testid={`emp-${i}-remove`}
                                className="sm:col-span-2 self-end text-[10px] font-black uppercase tracking-wider text-[#DC2626] hover:underline inline-flex items-center gap-1">
                                <Trash size={11} weight="bold" /> Remove
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Education */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] inline-flex items-center gap-1.5">
                        <GraduationCap size={12} weight="bold" /> Education
                    </p>
                    <button type="button" data-testid="add-education-btn" onClick={addEdu}
                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:underline inline-flex items-center gap-1">
                        <Plus size={11} weight="bold" /> Add
                    </button>
                </div>
                {education.length === 0 && (
                    <p className="text-xs text-[#A3A3A3] mb-2">Add at least your highest degree.</p>
                )}
                <div className="space-y-3">
                    {education.map((edu, i) => (
                        <div key={i} data-testid={`edu-row-${i}`} className="border border-[#E5E5E5] p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input value={edu.degree} onChange={(e) => updEdu(i, "degree", e.target.value)} placeholder="Degree (e.g. B.Tech)" data-testid={`edu-${i}-degree`}
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input value={edu.institute} onChange={(e) => updEdu(i, "institute", e.target.value)} placeholder="Institute" data-testid={`edu-${i}-institute`}
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input value={edu.field_of_study || ""} onChange={(e) => updEdu(i, "field_of_study", e.target.value)} placeholder="Field (optional)"
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input value={edu.grade || ""} onChange={(e) => updEdu(i, "grade", e.target.value)} placeholder="Grade / GPA (optional)"
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input type="number" min="1970" max="2035" value={edu.start_year || ""} onChange={(e) => updEdu(i, "start_year", e.target.value)} placeholder="Start year"
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <input type="number" min="1970" max="2035" value={edu.end_year || ""} onChange={(e) => updEdu(i, "end_year", e.target.value)} placeholder="End year"
                                className="px-2 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt" />
                            <button type="button" onClick={() => rmEdu(i)} data-testid={`edu-${i}-remove`}
                                className="sm:col-span-2 self-end text-[10px] font-black uppercase tracking-wider text-[#DC2626] hover:underline inline-flex items-center gap-1">
                                <Trash size={11} weight="bold" /> Remove
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <button type="button" data-testid="enrichment-save-btn" onClick={save} disabled={saving}
                className="px-4 py-2 text-xs font-black uppercase tracking-wider bg-ink text-white disabled:opacity-50">
                {saving ? "Saving…" : "Save details"}
            </button>
        </section>
    );
}

// ---------- KYC section (Phase 3) ---------------------------------------------
export function KycSection() {
    const [kyc, setKyc] = useState({ aadhar: {}, pan: {} });
    const [uploading, setUploading] = useState(null);

    const load = () => api.get("/me/profile/kyc").then((r) => setKyc(r.data || {})).catch(() => {});
    useEffect(() => { load(); }, []);

    const upload = async (docType, file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) { toast.error("Max 4MB."); return; }
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Upload a JPEG, PNG or WEBP image.");
            return;
        }
        setUploading(docType);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(
                `/me/profile/kyc/upload?doc_type=${docType}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            const s = r.data?.status;
            if (s === "verified") toast.success(`${docType.toUpperCase()} verified by AI.`);
            else if (s === "mismatch") toast.warning(`${docType.toUpperCase()} uploaded — name didn't match profile. Admin review pending.`);
            else toast.info(`${docType.toUpperCase()} uploaded — pending review.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(null);
        }
    };

    return (
        <section data-testid="kyc-section" className="bg-white border border-[#E5E5E5] p-5 sm:p-6 mb-6">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">Phase 3 · optional</p>
            <h2 className="font-heading text-2xl font-black tracking-tight text-ink">Verify identity</h2>
            <p className="text-xs text-[#525252] mt-1 mb-4">
                Upload your Aadhar and PAN — our AI reads them and marks you as verified. Only admins can view the documents.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <KycCard docType="aadhar" label="Aadhar card" doc={kyc.aadhar} uploading={uploading === "aadhar"} onUpload={upload} />
                <KycCard docType="pan" label="PAN card" doc={kyc.pan} uploading={uploading === "pan"} onUpload={upload} />
            </div>
        </section>
    );
}

function KycCard({ docType, label, doc, uploading, onUpload }) {
    const status = doc?.status;
    const badge = {
        verified: { cls: "bg-[#ECFDF5] text-[#059669]", Icon: CheckCircle, text: "Verified" },
        pending: { cls: "bg-[#FEF3C7] text-[#B45309]", Icon: Clock, text: "Pending review" },
        mismatch: { cls: "bg-[#FEF2F2] text-[#DC2626]", Icon: Warning, text: "Name mismatch" },
        failed: { cls: "bg-[#FEF2F2] text-[#DC2626]", Icon: Warning, text: "Failed" },
    }[status];
    return (
        <div data-testid={`kyc-card-${docType}`} className="border border-[#E5E5E5] p-4">
            <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-ink inline-flex items-center gap-1.5">
                    <IdentificationBadge size={14} weight="bold" className="text-[#525252]" />
                    {label}
                </p>
                {badge && (
                    <span data-testid={`kyc-${docType}-status`} className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 ${badge.cls}`}>
                        <badge.Icon size={11} weight="fill" /> {badge.text}
                    </span>
                )}
            </div>
            {doc?.id_number_last4 && (
                <p className="text-[11px] text-[#A3A3A3] mb-3 font-mono">•••• •••• {doc.id_number_last4}</p>
            )}
            <label className="block cursor-pointer">
                <input type="file" accept="image/jpeg,image/png,image/webp"
                    data-testid={`kyc-${docType}-upload`}
                    onChange={(e) => onUpload(docType, e.target.files?.[0])}
                    className="hidden" />
                <div className="w-full px-3 py-2 text-xs font-black uppercase tracking-wider bg-cobalt text-white text-center inline-flex items-center justify-center gap-1 hover:opacity-90">
                    <Upload size={12} weight="bold" />
                    {uploading ? "Verifying…" : (doc?.uploaded_at ? "Re-upload" : "Upload image")}
                </div>
            </label>
            {doc?.ai_analysis?.extracted?.name && (
                <p className="mt-2 text-[10px] text-[#525252]">
                    AI read: <strong>{doc.ai_analysis.extracted.name}</strong>
                    {doc.name_match ? " · matches profile ✓" : " · doesn't match profile"}
                </p>
            )}
        </div>
    );
}
